// Record pages: roster buyer firms on RecordPage (record-page redesign step 12;
// docs/record-page-redesign.md "### Company · roster buyer firm", "### Firm workspace",
// "### Summary band", build plan 12). views/company_page_v2.jsx renders these at its
// "firm variant (step 12)" seams through window.BuyerFirmV2 when the company carries
// buyer_id (CompanyRead) or a roster row; behind atlas.recordV2, never on a phone.
// The Firm workspace (?panel=firm&tab=…) is record_panels.jsx's `firm` entry.
// Cross-file names (window.BuyerFirm, window.BuyerLabels, window.Buyer*Body, the
// primitives) are read at render, never at module evaluation: buyer_detail.jsx and
// buyers_gpp.jsx load after this file. The pure helpers are window.BuyerFirmV2Kit
// (test/buyer_firm_v2.test.mjs).

// ── pure ──────────────────────────────────────────────────────────────────────
// The workspace tabs: [key, label, the roster-row field that counts it]. Buyer activity and
// Site changes count the NEW (unreviewed) items, so their counts read 'N new' (NEW_COUNTS):
// their tabs list the whole last year.
const FIRM_TABS = Object.freeze([
  ['deals', 'Deals', 'deals'], ['passes', 'Passes', 'passed'], ['team', 'Team', 'team_count'],
  ['portfolio', 'Portfolio', 'portfolio_count'], ['acquisitions', 'Acquisitions', 'acquisitions'],
  ['activity', 'Buyer activity', 'signals_new'], ['site', 'Site changes', 'changes_unreviewed'],
]);
const FIRM_TAB_KEYS = Object.freeze(FIRM_TABS.map((t) => t[0]));
const NEW_COUNTS = Object.freeze(['activity', 'site']);
// Band tile → the pop-out it opens: [panel, params] (Passed → the Passes tab, the matching one).
const BAND_TARGET = Object.freeze({
  deals: ['firm', { tab: 'deals' }], ndas: ['firm', { tab: 'deals' }], passed: ['firm', { tab: 'passes' }], band: ['details', null],
  portfolio: ['firm', { tab: 'portfolio' }], team: ['firm', { tab: 'team' }], acquisitions: ['firm', { tab: 'acquisitions' }], contacts: ['people', null],
});
const BAND_LABELS = Object.freeze(['Deals sent', 'NDAs', 'Passed', 'EBITDA band', 'Portfolio', 'Team', 'Acquisitions', 'Contacts']);
// = company_page_v2.jsx CARD_IDS.buyer_firm (the FoldAllPair scope; the node test cross-checks).
const FIRM_CARD_IDS = Object.freeze(['company.pursuits', 'company.passes', 'company.firm', 'company.deals', 'company.files', 'company.calls']);
// The Firm card's chips: [tab, label, the roster-row field].
const FIRM_CHIPS = Object.freeze([['team', 'Team', 'team_count'], ['portfolio', 'Portfolio', 'portfolio_count'],
  ['acquisitions', 'Acquisitions', 'acquisitions'], ['activity', 'Buyer activity', 'signals_new'], ['site', 'Site changes', 'changes_unreviewed']]);
const PURSUITS_SHOWN = 5;
const PASSES_SHOWN = 3;

const isFirm = (company, buyer) => !!(buyer || (company && company._raw && company._raw.buyer_id != null));
const bandTarget = (key) => BAND_TARGET[key] || null;
const firmTab = (p) => (p && FIRM_TAB_KEYS.includes(p.tab) ? p.tab : 'deals');
const countOf = (b, f) => (b && b[f] != null ? b[f] : null);
const countText = (tab, n) => (n == null ? null : NEW_COUNTS.includes(tab) ? `${n} new` : n);
const tabCounts = (b) => Object.fromEntries(FIRM_TABS.map(([k, , f]) => [k, countText(k, countOf(b, f))]));
const firmChips = (b) => FIRM_CHIPS.map(([tab, label, f]) => { const n = countOf(b, f); return { tab, label, n, text: countText(tab, n) }; });
// The Deals tab's Active / Won / Passed: buyer_detail.jsx bdDealStatus (window.BuyerFirm.dealStatus, read at call time).
const pursuitStatus = (d) => { const f = (window.BuyerFirm || {}).dealStatus; return f ? f(d) : null; };
// Live = active and the seller deal not archived (= the roster row's live_pursuits, the band's 'N live');
// the first `max` shown, `total` = every row (the Deals tab's All).
const pursuitRows = (rows, max = PURSUITS_SHOWN) => {
  const all = Array.isArray(rows) ? rows : [];
  const live = all.filter((d) => pursuitStatus(d) === 'active' && !d.engagement_archived);
  return { live, shown: live.slice(0, max), total: all.length };
};
// The last passes, newest first by passed_at (/for-company: the pursuit's exit, else the row's last
// write); rows without one go after, in the server's order. Each shows its deal and reason.
const passedAt = (d) => { const t = Date.parse(d.passed_at || ''); return Number.isNaN(t) ? -Infinity : t; };
const passRows = (rows, max = PASSES_SHOWN) => {
  const all = (Array.isArray(rows) ? rows : []).filter((d) => pursuitStatus(d) === 'passed')
    .sort((a, b) => { const x = passedAt(a); const y = passedAt(b); return x === y ? 0 : y > x ? 1 : -1; });
  return { all, shown: all.slice(0, max) };
};
const passReason = (d, reasons) => {
  if (!d.pass_reason) return d.pass_detail || d.notes || null;
  if (d.pass_reason === 'other') return d.pass_detail || d.notes || 'other';
  const o = (reasons || []).find((r) => r.value === d.pass_reason);
  return o ? o.label : String(d.pass_reason).replace(/_/g, ' ');
};
const profilePreview = (b) => {
  if (!b) return '—';
  const L = window.BuyerLabels || {};
  return [(L.BUYER_TYPE_LABEL || {})[b.buyer_type] || b.buyer_type, L.fmtBuyerBand ? L.fmtBuyerBand(b.min_ebitda, b.max_ebitda) : null, b.relationship_owner]
    .filter(Boolean).join(' · ') || '—';
};
const chipsPreview = (b) => (b ? firmChips(b).map((c) => `${c.label} ${c.text == null ? '—' : c.text}`).join(' · ') : '—');
const fmtD = (v) => (v ? (window.fmtDate ? window.fmtDate(v) : String(v).slice(0, 10)) : null);
const openPanel = (name, params) => window.openRecordPanel(name, params || {}, { via: 'button' });

// ── styles (FR_* = company_page_v2.jsx KeyRow's look; it is not exported) ───────
const DIM = { color: 'var(--text-4)' };
const LINK = { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 11.5, fontFamily: 'inherit', textAlign: 'left' };
const ELLIPSIS = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
const ROW_BTN = { background: 'transparent', border: 'none', padding: '6px 14px', cursor: 'pointer', font: 'inherit', fontSize: 12, color: 'var(--text-2)',
  display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', minWidth: 0 };
const CHIP = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--text)', fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit' };
const TILE_BOX = { display: 'flex', flex: '0 0 auto', minWidth: 118 };
const FR_ROW = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', minHeight: 30 };
const FR_LABEL = { width: 110, fontSize: 11.5, color: 'var(--text-3)', flexShrink: 0 };
const FR_VALUE = { flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text)', display: 'inline-flex', alignItems: 'center', gap: 5, overflow: 'hidden', textOverflow: 'ellipsis' };

// One Investment-profile row (label · value · after); empty = dimmed '—'.
const FirmRow = ({ label, value, after = null }) => (
  <div style={FR_ROW}>
    <span style={FR_LABEL}>{label}</span>
    <span style={FR_VALUE}>
      {value == null || value === '' || value === '—' ? <span style={DIM}>—</span> : value}
      {after}
    </span>
  </div>
);

// ── the band (RecordHeader `band`): BuyerFirm.Kpis as clickable tiles, 56px, one nowrap row.
// Deals sent's 'N live' is the roster row's live_pursuits (= the Pursuits card's count).
// Before the roster row is in: the eight tiles with '—' (same boxes, no width jump).
const FirmBand = ({ buyer, contacts }) => {
  const BF = window.BuyerFirm;
  const pick = (key) => { const t = bandTarget(key); if (t) openPanel(t[0], t[1]); };
  const n = contacts ? contacts.length : '—';
  return (
    <div data-firm-band="" style={{ height: 56, display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start', overflowX: 'auto', overflowY: 'hidden' }}>
      {buyer && BF && BF.Kpis ? <BF.Kpis buyer={buyer} contacts={n} onPick={pick} live={buyer.live_pursuits} />
        : BAND_LABELS.map((l) => <div key={l} style={TILE_BOX}><window.DKpi label={l} value={<span style={DIM}>—</span>} /></div>)}
    </div>
  );
};

// ── left: Investment profile (Section company.key_facts, the firm variant). Saves go
// through onSaveBuyer (PATCH /buyers/one) except Website (the company row). The research
// controller is this column's own: CompanyLeft (the other one) is not mounted on a firm.
const FirmLeft = ({ company, buyer, users, canWrite, onSaveBuyer, onSave, onOpenContact }) => {
  const rs = window.useFieldResearch('company', company.id);
  const EC = window.EditableCell;
  const BF = window.BuyerFirm || {};
  const L = window.BuyerLabels || {};
  const raw = company._raw || {};
  const site = raw.website || '';
  const b = buyer || null;
  const saveSite = (website) => onSave({ website: website || null });
  const footer = (
    <div style={{ padding: '6px 14px 10px' }}>
      <button type="button" data-all-details="" onClick={() => openPanel('details')} style={LINK}>All details ›</button>
    </div>
  );
  const agreementDates = b && (b.pba_sign_date || b.pba_expiration_date)
    ? <span style={{ fontSize: 11, ...DIM, whiteSpace: 'nowrap' }}>{[b.pba_sign_date && `signed ${fmtD(b.pba_sign_date)}`, b.pba_expiration_date && `expires ${fmtD(b.pba_expiration_date)}`].filter(Boolean).join(' · ')}</span>
    : null;
  return (
    <window.Section variant="rail" title="Investment profile" collapsible id="company.key_facts" preview={profilePreview(b)}>
      {buyer === undefined ? <div style={{ padding: '6px 14px' }}><window.SkeletonRows rows={6} /></div> : !b ? <div style={{ padding: '8px 14px', ...DIM }}>—</div> : (
        <div data-firm-profile="">
          <FirmRow label="Type" value={<EC type="select" value={b.buyer_type} options={BF.TYPE_OPTIONS || []} editable={canWrite} onSave={(buyer_type) => onSaveBuyer({ buyer_type })} />} />
          <FirmRow label="Kind" value={b.firm_kind ? <window.Pill hue={(L.FIRM_KIND_HUE || {})[b.firm_kind] || 'slate'} sz="sm">{(L.FIRM_KIND_LABEL || {})[b.firm_kind] || b.firm_kind}</window.Pill> : null} />
          <FirmRow label="EBITDA band" value={L.fmtBuyerBand ? L.fmtBuyerBand(b.min_ebitda, b.max_ebitda) : null} />
          <FirmRow label="Revenue band" value={L.fmtBuyerBand ? L.fmtBuyerBand(b.min_revenue, b.max_revenue) : null} />
          <FirmRow label="Industries" value={(b.preferred_industries || []).join(', ')} />
          <FirmRow label="Geography" value={(b.geo_focus || []).join(', ')} />
          <FirmRow label="Relationship owner" value={<EC type="user" value={b.relationship_owner_id || null} users={users} fallbackLabel={b.relationship_owner} editable={canWrite} onSave={(relationship_owner_id) => onSaveBuyer({ relationship_owner_id })} />} />
          <FirmRow label="Agreement" after={agreementDates}
            value={<EC type="select" value={b.agreement_kind || ''} options={BF.AGREEMENT_OPTIONS || []} editable={canWrite} onSave={(agreement_kind) => onSaveBuyer({ agreement_kind: agreement_kind || null })} />} />
          <FirmRow label="Primary contact" value={b.primary_contact_id ? (
            <button type="button" onClick={() => onOpenContact && onOpenContact(b.primary_contact_id)} style={{ ...LINK, ...ELLIPSIS, fontSize: 12 }}>
              {b.primary_contact_name || '—'}{b.primary_contact_title ? <span style={DIM}>{` · ${b.primary_contact_title}`}</span> : null}
            </button>) : null} />
          <FirmRow label="Website" value={<EC type="url" value={site} locked={(raw.dc_synced_fields || []).includes('website')} editable={canWrite} onSave={saveSite} />}
            after={canWrite && window.ResearchField ? <window.ResearchField ctl={rs} field="website" label="Website" kind="url" value={site} onApply={saveSite} /> : null} />
          <FirmRow label="PB blast" value={<EC type="text" value={b.pb_blast_status || ''} editable={canWrite} onSave={(pb_blast_status) => onSaveBuyer({ pb_blast_status })} />} />
        </div>
      )}
      {footer}
    </window.Section>
  );
};

// The People card's header link (company_page_v2.jsx PeopleCard `extra`): the scraped team.
const FirmTeamLink = ({ buyer }) => (buyer && buyer.team_count
  ? <button type="button" data-firm-team="" onClick={() => openPanel('firm', { tab: 'team' })} style={LINK}>{`Team · ${buyer.team_count}`}</button>
  : null);

// ── right: Pursuits (company.pursuits), Passes (company.passes), Firm (company.firm).
// Opens the pursuit page (the buyer_buyside shell), else the seller deal. Hues = the Deals tab's.
const PursuitRow = ({ d, onOpen }) => {
  const BF = window.BuyerFirm || {};
  return (
    <div data-pursuit-row={d.id} style={{ padding: '6px 14px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
        <button type="button" onClick={() => onOpen && onOpen(d.pursuit_engagement_id || d.engagement_id)} style={{ ...LINK, ...ELLIPSIS, fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
          {d.seller_company_name || d.engagement_name || '—'}
        </button>
        {d.pursuit_stage && <window.Pill hue={window.dcStageHue ? window.dcStageHue(d.pursuit_stage) : 'slate'} sz="sm" dot>{window.cleanDcStage ? window.cleanDcStage(d.pursuit_stage) : d.pursuit_stage}</window.Pill>}
        {d.nda_status && d.nda_status !== 'not_sent' && <window.Pill hue={(BF.NDA_HUE || {})[d.nda_status] || 'slate'} sz="sm">{`NDA ${d.nda_status}`}</window.Pill>}
        {d.interest_level && <window.Pill hue={(BF.INTEREST_HUE || {})[d.interest_level] || 'slate'} sz="sm">{d.interest_level}</window.Pill>}
        {d.loi_submitted && <window.Pill hue="blue" sz="sm">LOI</window.Pill>}
        {d.bs_call_date && <window.Pill hue="teal" sz="sm">{`B/S call ${fmtD(d.bs_call_date)}`}</window.Pill>}
      </div>
      {d.next_action_date && <div className="num" style={{ fontSize: 11, ...DIM }}>{`Next action ${fmtD(d.next_action_date)}`}</div>}
    </div>
  );
};

const PursuitsCard = ({ rows, canWrite, onOpenDeal, onSend }) => {
  const { live, shown, total } = pursuitRows(rows);
  const action = (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      {canWrite && onSend && <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={onSend}>Send a deal</window.Btn>}
      <window.Btn size="xs" variant="ghost" onClick={() => openPanel('firm', { tab: 'deals' })}>{rows ? `All ${total}` : 'All —'}</window.Btn>
    </span>
  );
  return (
    <window.SummaryCard title="Pursuits" action={action} collapsible id="company.pursuits" preview={rows ? `${live.length} live` : '—'}>
      {rows == null ? <div style={{ padding: '10px 14px' }}><window.SkeletonRows rows={3} /></div>
        : !shown.length ? <div style={{ padding: '8px 14px', ...DIM }}>0</div>
        : <div style={{ padding: '4px 0 6px' }}>{shown.map((d) => <PursuitRow key={d.id} d={d} onOpen={onOpenDeal} />)}</div>}
    </window.SummaryCard>
  );
};

// Hidden until a pass exists; every row opens the Passes tab.
const PassesCard = ({ rows }) => {
  const { all, shown } = passRows(rows);
  if (!all.length) return null;
  const open = () => openPanel('firm', { tab: 'passes' });
  const reasons = window.BE_PASS_REASONS || [];
  return (
    <window.SummaryCard title={`Passes · ${all.length}`} collapsible id="company.passes" preview={`${all.length} passed`}
      action={<window.Btn size="xs" variant="ghost" icon={<window.Icon.Maximize size={11} />} title="Open" onClick={open} />}>
      {shown.map((d) => {
        const why = passReason(d, reasons);
        return (
          <button key={d.id} type="button" data-pass-row={d.id} onClick={open} style={ROW_BTN}>
            <span style={{ ...ELLIPSIS, flexShrink: 0, maxWidth: '55%', color: 'var(--text)', fontWeight: 500 }}>{d.seller_company_name || d.engagement_name || '—'}</span>
            <span style={{ ...ELLIPSIS, flex: 1, color: 'var(--text-3)' }}>{why || <span style={DIM}>—</span>}</span>
          </button>
        );
      })}
    </window.SummaryCard>
  );
};

const FirmChips = ({ buyer }) => (
  <window.SummaryCard title="Firm" collapsible id="company.firm" preview={chipsPreview(buyer)}>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 14px 12px' }}>
      {firmChips(buyer).map((c) => (
        <button key={c.tab} type="button" data-firm-chip={c.tab} onClick={() => openPanel('firm', { tab: c.tab })} style={CHIP}>
          {c.label}
          <span className="num" style={{ color: c.n ? 'var(--text-2)' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{c.text == null ? '—' : c.text}</span>
        </button>
      ))}
    </div>
  </window.SummaryCard>
);

// The pursuit rows come from CompanyDetail's buyerEngs (one read). A Send a deal bumps
// dealsTick without re-reading them (classic refetches only its DealsPanel), so the column
// re-reads on the tick and both cards follow.
const FirmRight = ({ companyId, buyer, buyerEngs, dealsTick, canWrite, onOpenDeal, onSend }) => {
  const [fresh, setFresh] = React.useState(null);
  React.useEffect(() => {
    if (!dealsTick) return undefined;
    let alive = true;
    window.AtlasApi.buyerEngagementsForCompany(companyId)
      .then((r) => { if (alive) setFresh((r && r.items) || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [companyId, dealsTick]);
  const rows = fresh || buyerEngs || null;
  return (
    <>
      <PursuitsCard rows={rows} canWrite={canWrite && !!buyer} onOpenDeal={onOpenDeal} onSend={onSend} />
      <PassesCard rows={rows} />
      <FirmChips buyer={buyer || null} />
    </>
  );
};

// The folded right column's strip entries for the three firm cards (company_page_v2.jsx
// appends Deals / Files / Calls).
const firmRails = () => {
  const Icon = window.Icon;
  return [
    { key: 'company.pursuits', icon: <Icon.Pipeline size={13} />, label: 'Pursuits', onOpen: () => openPanel('firm', { tab: 'deals' }) },
    { key: 'company.passes', icon: <Icon.Close size={13} />, label: 'Passes', onOpen: () => openPanel('firm', { tab: 'passes' }) },
    { key: 'company.firm', icon: <Icon.Diamond size={13} />, label: 'Firm', onOpen: () => openPanel('firm', { tab: 'team' }) },
  ];
};

// ── header parts (RecordHeader pills / primaries / meta) and the ⋯ items. Plain
// functions called in CompanyPageV2's render: no hook here.
// Pills: one pill per entry, in priority order, so HeaderPills (3 entries, the rest under '+N')
// does the capping: DNC (call safety: always the first) · the firm's pills (type, agreement,
// firm kind, domain suspect; BuyerFirm.headerPillList) · the role chips. Acquired? renders
// nothing until a record is pending, so it rides in the first entry instead of taking one:
// that entry is always the 'lead' fragment with Acquired? as its first child, so the pill (and
// its confirm drawer) keeps its node when the roster row lands.
const firmHeader = ({ company, buyer, canWrite, onSendDeal, onAcquisitionDecided, z }) => {
  const BF = window.BuyerFirm || {};
  const raw = company._raw || {};
  const roles = (raw.roles || []).filter((r) => !window.ROLE_META || window.ROLE_META[r]);
  const real = [
    raw.is_dnc ? <window.Pill key="dnc" hue="rose" sz="sm">DNC</window.Pill> : null,
    ...(buyer && BF.headerPillList ? BF.headerPillList(buyer) : []),
    roles.length ? <window.RoleChips key="roles" roles={raw.roles} /> : null,
  ].filter(Boolean);
  const acq = window.AcquisitionHeaderPill
    ? <window.AcquisitionHeaderPill companyId={company.id} companyName={company.name} hidden={!!raw.is_acquired} onDecided={onAcquisitionDecided} drawerZIndex={z} />
    : null;
  const pills = [<React.Fragment key="lead">{acq}{real[0] || null}</React.Fragment>, ...real.slice(1)];
  const primaries = canWrite
    ? [<window.Btn key="send" variant="primary" size="sm" icon={<window.Icon.Plus size={12} />} disabled={!buyer} onClick={onSendDeal}>Send a deal</window.Btn>]
    : [];
  const says = buyer && buyer.pass_profile && buyer.pass_profile.says_no_to;
  const meta = says
    ? <span data-firm-says="" style={{ minWidth: 0, ...ELLIPSIS }}><b style={{ color: 'var(--text-2)', fontWeight: 600 }}>Says no to:</b> {says}</span>
    : null;
  return { pills, primaries, meta };
};
// = CompanyPageKit.menuKeys('buyer_firm'): Add contact · New engagement · Edit investment profile · History.
const firmMenuItems = ({ drawers }) => [
  { key: 'add_contact', label: 'Add contact', onSelect: () => drawers.setAddingContact(true) },
  { key: 'new_engagement', label: 'New engagement', onSelect: () => drawers.setAddingEngagement(true) },
  { key: 'edit_profile', label: 'Edit investment profile', onSelect: () => openPanel('details') },
  { key: 'history', label: 'History', onSelect: () => openPanel('history') },
];

// ── the Firm workspace (?panel=firm&tab=…): the classic bodies, one tab at a time (each
// reads on mount), bare where they have a bare mode (the tab is their title and count; no
// empty-state prose; Passes without its 'Says no to' repeat). ctx.buyer is the roster row
// (the registry's applies() waits for it).
const FirmWorkspace = ({ api }) => {
  const company = api.record || {};
  const ctx = api.ctx || {};
  const buyer = ctx.buyer || null;
  const tab = firmTab(api.params);
  const counts = tabCounts(buyer);
  const tabs = FIRM_TABS.map(([key, label]) => ({ key, label, count: counts[key] }));
  const BF = window.BuyerFirm || {};
  const goDeal = (id) => {
    if (!id) return;
    if (ctx.setDealId) ctx.setDealId(id);
    else if (window.AtlasNav && window.AtlasNav.goToDeal) window.AtlasNav.goToDeal(id);
  };
  const goCompany = (id) => { if (id && window.AtlasNav && window.AtlasNav.goToCompany) window.AtlasNav.goToCompany(id); };
  let body = null;
  if (buyer) {
    if (tab === 'deals') body = BF.DealsPanel ? <BF.DealsPanel buyer={buyer} onOpenDeal={goDeal} tick={ctx.dealsTick || 0} onSend={api.canWrite && ctx.onSendDeal ? ctx.onSendDeal : undefined} bare /> : null;
    else if (tab === 'passes') body = BF.PassesPanel ? <BF.PassesPanel buyer={buyer} bare /> : null;
    else if (tab === 'team') body = window.BuyerTeamBody ? <window.BuyerTeamBody buyer={buyer} /> : null;
    else if (tab === 'portfolio') body = window.BuyerPortfolioBody ? <window.BuyerPortfolioBody buyer={buyer} setCompanyId={goCompany} /> : null;
    else if (tab === 'acquisitions') body = BF.AcquisitionsPanel ? <BF.AcquisitionsPanel buyer={buyer} setCompanyId={goCompany} bare /> : null;
    else if (tab === 'activity') body = BF.SignalsPanel ? <BF.SignalsPanel buyer={buyer} bare /> : null;
    else if (tab === 'site') body = BF.ChangesPanel ? <BF.ChangesPanel buyer={buyer} bare /> : null;
  }
  return (
    <window.FullScreenPanel active={api.active} title={`${company.name || '—'} › Firm`} maxWidth={1480}
      tabs={tabs} tab={tab} onTab={(k) => api.set({ tab: k })}
      onClose={() => api.close({ via: 'button' })} onEscape={api.escape}>
      <div data-firm-workspace={tab} style={{ padding: 16, maxWidth: 960, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        {body || <div style={{ fontSize: 12.5, ...DIM }}>—</div>}
      </div>
    </window.FullScreenPanel>
  );
};

Object.assign(window, {
  FirmWorkspace,
  BuyerFirmV2: Object.freeze({ isFirm, header: firmHeader, menuItems: firmMenuItems, Band: FirmBand, Left: FirmLeft, TeamLink: FirmTeamLink,
    Right: FirmRight, rails: firmRails, CARD_IDS: FIRM_CARD_IDS }),
  BuyerFirmV2Kit: Object.freeze({ FIRM_TABS, FIRM_TAB_KEYS, NEW_COUNTS, BAND_TARGET, BAND_LABELS, CARD_IDS: FIRM_CARD_IDS, isFirm, bandTarget, firmTab,
    countText, tabCounts, firmChips, chipsPreview, pursuitStatus, pursuitRows, passRows, passReason, profilePreview }),
});
